import { AccountAllocationGrid } from '@components/AccountAllocationGrid';
import { datadogLogs } from '@datadog/browser-logs';
import type { TranslatedString } from '@interfaces/shared';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { Typography } from 'hephaestus/web';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  HouseholdModelPortfolioDraftSkeleton,
  HouseholdModelPortfolioDraftUnavailable,
} from '../householdModelPortfolioDraft/householdModelPortfolioDraftStates';
import { useEditAccountAllocations } from './hooks/useEditAccountAllocations';

const TEST_ID = 'edit-account-allocations';

type EditAccountAllocationsOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
};

export type EditAccountAllocationsProp = {
  options: EditAccountAllocationsOptions;
  clientGroupId?: string;
  onNext: (branchId?: string) => void;
  stepLoading?: boolean;
};

export const EditAccountAllocations = ({
  options,
  clientGroupId,
  onNext,
  stepLoading = false,
}: EditAccountAllocationsProp) => {
  const { translateBackendString } = useLocalization();
  const { workflowData } = useContext(WorkflowContext);
  const { t } = useTranslation('workflowCompletions');

  const householdClientGroupId =
    workflowData.currentHouseholdClientGroupId ?? clientGroupId;

  const { report, loading, error, refetch } = useEditAccountAllocations(
    householdClientGroupId,
  );

  useEffect(() => {
    if (!error) return;
    datadogLogs.logger.error(
      'Edit account allocations load failed',
      { clientGroupId: householdClientGroupId },
      error,
    );
  }, [error, householdClientGroupId]);

  const continueWithoutStaging = () => onNext();

  const retry = () => {
    refetch().catch((retryError: unknown) => {
      datadogLogs.logger.error(
        'Edit account allocations retry failed',
        { clientGroupId: householdClientGroupId },
        retryError instanceof Error ? retryError : undefined,
      );
    });
  };

  const title = translateBackendString(options?.title);

  if (loading || stepLoading) {
    return (
      <HouseholdModelPortfolioDraftSkeleton title={title} testId={TEST_ID} />
    );
  }

  if (error || !report) {
    return (
      <HouseholdModelPortfolioDraftUnavailable
        title={title}
        message={t('editAccountAllocations.unavailable')}
        testId={TEST_ID}
        onRetry={error ? retry : undefined}
      />
    );
  }

  return (
    <>
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {title}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackendString(options.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}
      <AccountAllocationGrid report={report} onSave={continueWithoutStaging} />
    </>
  );
};
